import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Activity, BarChart3, Blocks, Brain, ChevronDown, ChevronLeft, ChevronRight,
  CircleDot, Database, Flower2, GitBranch, LayoutGrid, LayoutPanelLeft,
  Layers3, Loader2, Menu, MessageSquareText, PieChart as PieChartIcon,
  RefreshCcw, Search, SendHorizontal, Settings2, Sparkles, Table2,
  Trash2, TrendingUp, UserCircle2, TrendingDown, Activity as DiagnosisIcon,
} from './icons'

import { api } from './api'
import './index.css'
import { MetadataStudioModal } from './components/MetadataStudioModal'
import AnimatedInputOverlay from './components/AnimatedInputOverlay'
import { VisualizationSwitcher } from './components/VisualizationViews'
import { CheckboxRow, ColorField, LabeledSelect, NumberField, PanelSection } from './components/FormControls'
import { useSuggestions } from './hooks/useSuggestions'
import AudioRecorderButton from './components/AudioRecorderButton'
import { useWorkspaceBootstrap } from './hooks/useWorkspaceBootstrap'
import { useSemanticSessions } from './hooks/useSemanticSessions'
import { SessionSidebar } from './components/SessionSidebar'
import { NormalizationBanner } from './components/NormalizationBanner'
import { FeedbackButtons } from './components/FeedbackButtons'
import DashboardPanelSwitcher from '../Common/DashboardPanelSwitcher'
import {
  applySuggestion, createDefaultConfig, formatMetric,
  getFieldKeys, getNumericKeys, humanize, promptToTitle,
  COLOR_PALETTES, looksLikeSalesValueColumn,
} from './lib/queryWorkspaceUtils'
import {
  buildCardsFromSessionMessages, normalizeAssistantResponse,
} from './lib/sessionMessageNormalizer'
import { formatErrorMessage, normalizeApiError } from '../../utils/apiErrors'
import { getUserEmail } from '../../utils/userUtils'


const VIEW_ITEMS = [
  { value: 'table', label: 'Table', icon: Table2 },
  { value: 'bar', label: 'Bar', icon: BarChart3 },
  { value: 'line', label: 'Line', icon: TrendingUp },
  { value: 'area', label: 'Area', icon: Activity },
  { value: 'combo', label: 'Bar + Line', icon: Layers3 },
  { value: 'stackedBar', label: 'Stacked', icon: Layers3 },
  { value: 'pie', label: 'Pie', icon: PieChartIcon },
  { value: 'donut', label: 'Donut', icon: CircleDot },
  { value: 'rose', label: 'Rose', icon: Flower2 },
  { value: 'donutRose', label: 'Donut Rose', icon: CircleDot },
  { value: 'quadrant', label: 'Quadrant', icon: GitBranch },
  { value: 'treemap', label: 'Tree', icon: Blocks },
  { value: 'waterfall', label: 'Waterfall', icon: BarChart3 },
]

const ORIENTATIONS = [
  { value: 'vertical', label: 'Vertical' },
  { value: 'horizontal', label: 'Horizontal' },
]

const CUSTOMIZE_TABS = [
  { value: 'display', label: 'Display' },
  { value: 'mapping', label: 'Fields' },
  { value: 'chartView', label: 'Chart View' },
]

const ANALYSIS_TABS = [
  { value: 'summary', label: 'Summary', icon: Sparkles },
  { value: 'analysis', label: 'Analysis', icon: MessageSquareText },
  { value: 'context', label: 'Context', icon: Database },
]

// ── Friday embedded semantic workspace
export default function SemanticWorkspace({ initialTab = 'workspace' }) {
  return <SemanticQueryApp initialTab={initialTab} />
}

/**
 * Sanitized, display-ready text for any thrown value (Req 10.1, 10.2).
 *
 * `api.js` already rejects with a formatted message and the descriptor on
 * `apiError`, but errors raised outside that layer (a normalizer throwing, an
 * aborted fetch) reach the UI unnormalized. Re-deriving the string here means the
 * reference ID is appended exactly once and raw payload text is never rendered.
 *
 * @param {unknown} error
 * @returns {string}
 */
function toDisplayMessage(error) {
  const descriptor =
    error && typeof error === 'object' && error.apiError
      ? error.apiError
      : normalizeApiError(error)
  return formatErrorMessage(descriptor)
}

/**
 * Applies the shared QueryResult defaults to a live query response.
 *
 * Both paths now go through `normalizeAssistantResponse`, so the restored and the
 * live card carry identical shapes for `data.*`, `query`, `timing`,
 * `visualization`, and `normalization` (Req 5.1 - 5.5). Original keys are kept:
 * the normalized values are merged *over* the response, so extra live-only fields
 * such as `data.column_formats` survive for `createDefaultConfig`.
 *
 * The response's own `session_id` wins over the workspace state, which is still
 * null for the first query of a brand-new session.
 *
 * @param {object} response live `/query` response
 * @param {string|null} fallbackSessionId current workspace session id
 * @returns {object} response with the shared defaults applied
 */
function withLiveResponseDefaults(response, fallbackSessionId = null) {
  const sessionId = response?.session_id ?? fallbackSessionId ?? null
  const normalized = normalizeAssistantResponse(response, sessionId)
  return {
    ...(response || {}),
    ...normalized,
    data: { ...(response?.data || {}), ...normalized.data },
  }
}

// ── Semantic Query workspace (original v10 app) ───────────────────────────────
function SemanticQueryApp({ initialTab = 'workspace' }) {
  const [cards, setCards] = useState([])
  const [message, setMessage] = useState('')
  const [aiMode, setAiMode] = useState(false)
  const [activeTab, setActiveTab] = useState(initialTab)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [workspaceMicStatus, setWorkspaceMicStatus] = useState('idle')
  const [profileOpen, setProfileOpen] = useState(false)
  const [metadataStudioOpen, setMetadataStudioOpen] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const {
    status, catalog, metadata, synonyms, setSynonyms,
    businessSemantic, setBusinessSemantic,
    loadingStatus, globalError, setGlobalError, bootstrap,
  } = useWorkspaceBootstrap()
  const { suggestions, activeSuggestionIndex, setActiveSuggestionIndex, clearSuggestions } = useSuggestions(message, synonyms, metadata, businessSemantic)

  // ── Session management ────────────────────────────────────────────────────
  const {
    sessionId, sessions, sessionsLoading, fetchSessions,
    loadSession, loadedMessages, setLoadedMessages, messagesLoading,
    deleteSession, startNewSession, updateSessionFromResponse, submitFeedback,
  } = useSemanticSessions()

  useEffect(() => { bootstrap() }, [bootstrap])

  useEffect(() => { setActiveTab(initialTab) }, [initialTab])

  async function handleSend(customMessage = null) {
    const trimmed = (customMessage ?? message).trim()
    if (!trimmed || isSubmitting || ['recording', 'paused', 'transcribing'].includes(workspaceMicStatus)) return
    setIsSubmitting(true)
    setGlobalError('')
    try {
      const userId = getUserEmail() || null
      const payload = {
        query: trimmed,
        mode: aiMode ? 'ai' : 'rule',
        page: 1,
        page_size: 25,
        session_id: sessionId || null,
        user_id: userId,
      }
      const liveResponse = await api.query(payload)
      // Track session from response
      updateSessionFromResponse(liveResponse)
      // Same defaulting helper as the restore path, so a live response and a
      // restored one can never drift in shape (Req 5.1 - 5.5).
      const response = withLiveResponseDefaults(liveResponse, sessionId)
      const rows = response.data.rows
      const isMessageResult = response.result_type === "message";
      const card = {
        id: crypto.randomUUID(),
        query: trimmed,
        mode: response.mode,
        page: response.data.page,
        pageSize: response.data.page_size,
        response,
        data: rows,
        config: createDefaultConfig(rows, trimmed, response),
        // New fields for session/feedback/normalization
        messageId: response.message_id,
        sessionId: response.session_id,
        normalization: response.normalization,
        // Chat-style message result (no table/chart)
        isMessage: isMessageResult,
        message: response.message,
      }
      setCards((prev) => [card, ...prev])
      setMessage('')
      clearSuggestions()
    } catch (error) {
      const errorCard = buildErrorCard(trimmed, aiMode ? 'ai' : 'rule', error)
      setCards((prev) => [errorCard, ...prev])
      setMessage('')
      clearSuggestions()
    } finally {
      setIsSubmitting(false)
    }
  }

  async function reloadCard(cardId, page) {
    const target = cards.find((c) => c.id === cardId)
    if (!target) return
    try {
      const userId = getUserEmail() || null
      const pageResponse = await api.query({
        query: target.query,
        mode: target.mode,
        page,
        page_size: target.pageSize || 25,
        session_id: sessionId || null,
        user_id: userId,
      })
      const response = withLiveResponseDefaults(pageResponse, sessionId)
      setCards((prev) =>
        prev.map((c) =>
          c.id === cardId
            ? {
                ...c, response,
                data: response.data.rows || [],
                page: response.data.page,
                messageId: response.message_id || c.messageId,
                normalization: response.normalization || c.normalization,
                config: {
                  ...c.config,
                  title: promptToTitle(response?.visualization?.title || c.query),
                  viewType: c.config.viewType === 'table' ? response.visualization?.resolved_view || 'table' : c.config.viewType,
                },
              }
            : c,
        ),
      )
    } catch (error) {
      setGlobalError(toDisplayMessage(error))
    }
  }

  /**
   * Handle selecting a session from the sidebar.
   *
   * The mapping lives in `lib/sessionMessageNormalizer.js`, which the live path
   * also uses, so restored history renders through exactly the same code as a
   * fresh query result (Req 5.1 - 5.5).
   */
  async function handleSelectSession(targetSessionId) {
    const sessionData = await loadSession(targetSessionId)
    if (!sessionData?.messages?.length) return
    setCards(buildCardsFromSessionMessages(sessionData.messages, targetSessionId))
    setSidebarOpen(false)
  }

  /** Handle new session from sidebar */
  function handleNewSession() {
    startNewSession()
    setCards([])
  }

  function handleKeyDown(event) {
    if ((event.key === 'Enter' || event.key === 'Tab') && suggestions.length > 0) {
      event.preventDefault()
      const suggestion = suggestions[activeSuggestionIndex] || suggestions[0]
      if (suggestion) {
        setMessage((current) => `${applySuggestion(current, suggestion)} `)
        clearSuggestions()
      }
      return
    }
    if (event.key === 'ArrowUp' && suggestions.length > 0) {
      event.preventDefault()
      setActiveSuggestionIndex((current) => (current - 1 + suggestions.length) % suggestions.length)
      return
    }
    if (event.key === 'ArrowDown' && suggestions.length > 0) {
      event.preventDefault()
      setActiveSuggestionIndex((current) => (current + 1) % suggestions.length)
      return
    }
    if (event.key === 'Escape' && suggestions.length > 0) { clearSuggestions(); return }
    if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); handleSend() }
  }

  function applyTranscriptToMessage(transcript) {
    setMessage((current) => current.trim() ? `${current.trim()} ${transcript}` : transcript)
  }

  function updateCardConfig(id, patch) {
    setCards((prev) => prev.map((c) => c.id === id ? { ...c, config: { ...c.config, ...patch } } : c))
  }
  function removeCard(id) { setCards((prev) => prev.filter((c) => c.id !== id)) }

  async function saveBusinessSemantic(payload) {
    try {
      const saved = await api.saveBusinessSemantic(payload)
      setBusinessSemantic(saved)
      await bootstrap()
    } catch (error) { setGlobalError(toDisplayMessage(error)); throw error }
  }

  async function saveSynonym(kind, payload) {
    try {
      if (kind === 'measure')   await api.saveMeasureSynonyms(payload)
      if (kind === 'dimension') await api.saveDimensionSynonyms(payload)
      if (kind === 'value')     await api.saveValueSynonyms(payload)
      const synonymPayload = await api.getSynonyms()
      setSynonyms(synonymPayload)
    } catch (error) { setGlobalError(toDisplayMessage(error)) }
  }

  return (
    <div className="semantic-ui-root min-h-screen bg-[#f5f6f8] text-slate-900">
      <TopBar
        profileOpen={profileOpen}
        setProfileOpen={setProfileOpen}
        onOpenStudio={() => { setMetadataStudioOpen(true); setProfileOpen(false) }}
        onReload={bootstrap}
        loadingStatus={loadingStatus}
        status={status}
        activeTab={activeTab}
      />

      {/* Session History Sidebar + Main Content in flex row */}
      <div className="flex min-h-0 flex-1">
        <SessionSidebar
          open={sidebarOpen}
          onToggle={() => setSidebarOpen((v) => !v)}
          sessions={sessions}
          sessionsLoading={sessionsLoading}
          currentSessionId={sessionId}
          onSelectSession={handleSelectSession}
          onDeleteSession={deleteSession}
          onNewSession={handleNewSession}
          onRefresh={fetchSessions}
        />

      <div className="min-w-0 flex-1 mx-auto max-w-[1520px] px-3 pb-36 pt-5 md:px-5">
        {/* Tab navigation */}
        <div className="mb-5 flex items-center gap-1 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('workspace')}
            className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition ${
              activeTab === 'workspace' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <MessageSquareText className="h-4 w-4" />
            Query Workspace
          </button>
        </div>

        {activeTab === 'workspace' && (
          <>
            {globalError ? <AlertBanner message={globalError} /> : null}
            <header className="mb-5 flex flex-wrap items-center justify-between gap-3 px-1">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">
                  Semantic Query Workspace
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  Query the full semantic model in model mode or AI mode, then explore the result as a chart or table.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSidebarOpen((v) => !v)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
                >
                  {sidebarOpen ? 'Hide History' : '📋 History'}
                </button>
                <div className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500 shadow-sm">
                  {status?.datasetId ? `Dataset: ${status.datasetId}` : 'Dataset not configured yet'}
                </div>
              </div>
            </header>

            {cards.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="space-y-5">
                {cards.map((card) => (
                  <DashboardCard
                    key={card.id}
                    card={card}
                    onChange={(patch) => updateCardConfig(card.id, patch)}
                    onDelete={() => removeCard(card.id)}
                    onPageChange={(page) => reloadCard(card.id, page)}
                    onSubmitFeedback={submitFeedback}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      </div>{/* end flex row */}

      {activeTab === 'workspace' ? (
      <ChatComposer
        message={message}
        setMessage={setMessage}
        onSend={handleSend}
        onKeyDown={handleKeyDown}
        suggestions={suggestions}
        activeSuggestionIndex={activeSuggestionIndex}
        onSuggestionSelect={(suggestion) => {
          setMessage((current) => `${applySuggestion(current, suggestion)} `)
          clearSuggestions()
        }}
        onDismissSuggestions={clearSuggestions}
        aiMode={aiMode}
        setAiMode={setAiMode}
        isSubmitting={isSubmitting}
        micStatus={workspaceMicStatus}
        onMicStatusChange={setWorkspaceMicStatus}
        onTranscript={applyTranscriptToMessage}
      />
      ) : null}

      {metadataStudioOpen ? (
        <MetadataStudioModal
          onClose={() => setMetadataStudioOpen(false)}
          catalog={catalog}
          metadata={metadata}
          synonyms={synonyms}
          onSave={saveSynonym}
          businessSemantic={businessSemantic}
          onSaveBusinessSemantic={saveBusinessSemantic}
        />
      ) : null}
    </div>
  )
}

// ── TopBar with enhanced profile dropdown ─────────────────────────────────────
function TopBar({ profileOpen, setProfileOpen, onOpenStudio, onReload, loadingStatus, status, activeTab }) {
  const navigate   = useNavigate()
  const location   = useLocation()
  const dropRef    = useRef(null)
  const title       = 'Emcure Semantic Query'
  const queryPath   = location.pathname.startsWith('/dashboard') ? '/dashboard/semantic-query' : '/semantic-query-engine'

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) setProfileOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [setProfileOpen])

  return (
    <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-[1520px] items-center justify-between px-4 py-3 md:px-5">
        {/* Left: logo + title */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
            <LayoutGrid className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            <div className="text-xs text-slate-500">
              {status?.semanticConnected ? 'Semantic query engine connected' : status?.semanticConfigured ? 'Semantic query engine configured' : 'Waiting for credentials'}
              {' · '}
              {status?.bedrockConfigured ? 'Bedrock ready' : 'Bedrock optional'}
            </div>
          </div>
        </div>

        {/* Right: panel switcher + reload + profile */}
        <div className="flex items-center gap-2">
          <DashboardPanelSwitcher direction="down" />
          <div className="relative" ref={dropRef}>
            <div className="flex items-center gap-2">
              <button
                onClick={onReload}
                className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 transition hover:bg-slate-50"
                title="Reload metadata and health"
              >
                {loadingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
              </button>
              <button
                onClick={() => setProfileOpen((v) => !v)}
                className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                <UserCircle2 className="h-5 w-5" />
                <span>Profile</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>

          {/* ── Profile dropdown ─────────────────────────────────────── */}
          {profileOpen && (
            <div className="absolute right-0 mt-2 w-72 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
              {/* Metadata Studio */}
              <button
                onClick={onOpenStudio}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <Database className="h-4 w-4 text-slate-500" />
                <div>
                  <div className="font-medium">Metadata Studio</div>
                  <div className="text-xs text-slate-400">Manage synonyms and semantic rules</div>
                </div>
              </button>

              {/* Back to query workspace if on another semantic route */}
              {location.pathname !== '/' && (
                <>
                  <div className="my-2 border-t border-slate-100" />
                  <button
                    onClick={() => { navigate(queryPath); setProfileOpen(false) }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50"
                  >
                    <MessageSquareText className="h-4 w-4" />
                    <span>Back to Semantic Query</span>
                  </button>
                </>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  )
}

function AlertBanner({ message }) {
  return (
    <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
      {message}
    </div>
  )
}

/**
 * Card shown when a query fails.
 *
 * The user-facing copy stays deliberately generic; the sanitized descriptor text —
 * including "(Reference: <id>)" when the backend supplied one — goes into the
 * collapsible technical-details block, so no raw database detail is ever the
 * headline (Req 10.1, 10.2).
 */
function buildErrorCard(query, mode, error) {
  return {
    id: crypto.randomUUID(),
    query,
    mode,
    isError: true,
    errorMessage: 'Please rephrase your question in a different way and try again.',
    technicalMessage: toDisplayMessage(error),
    config: {
      title: promptToTitle(query),
      viewType: 'table',
      panelOpen: false,
      analysisTab: 'summary',
    },
  }
}

function getWorkspaceActivityState({ micStatus, isSubmitting }) {
  if (isSubmitting) {
    return {
      active: true,
      title: 'Fetching results…',
      subtitle: 'Waiting for the backend and Databricks response.',
    }
  }

  if (micStatus === 'recording') {
    return {
      active: true,
      title: 'Listening to your question…',
      subtitle: 'Microphone is active. Pause or stop when you are done speaking.',
    }
  }

  if (micStatus === 'paused') {
    return {
      active: true,
      title: 'Recording paused',
      subtitle: 'Resume when ready, or stop to convert your speech to English.',
    }
  }

  if (micStatus === 'transcribing') {
    return {
      active: true,
      title: 'Converting speech to English…',
      subtitle: 'Your transcript will appear in the field after conversion finishes.',
    }
  }

  return { active: false, title: '', subtitle: '' }
}

function EmptyState() {
  return (
    <div className="rounded-[28px] border border-dashed border-slate-300 bg-white px-6 py-12 text-center shadow-sm">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-100 text-slate-500">
        <Search className="h-7 w-7" />
      </div>
      <h2 className="mt-4 text-xl font-semibold text-slate-900">Ask your first question</h2>
      <p className="mt-2 text-sm text-slate-500">
        Try: "Show total sales by HQ for March 2026 as bar chart" or "Coverage by state this month".
      </p>
    </div>
  )
}

function MessageResultCard({ card, onDelete }) {
  const messageText = card.message || card.response?.message || "No message returned.";
  return (
    <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3 md:px-5">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-700">{promptToTitle(card.query)}</div>
          <span className="mt-1 inline-block rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] text-slate-600">
            {card.mode === 'ai' ? 'AI mode' : 'Model mode'}
          </span>
        </div>
        <button onClick={onDelete} className="rounded-xl p-2 text-rose-500 transition hover:bg-rose-50" title="Delete card">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="px-4 py-4 md:px-5">
        <div className="rounded-2xl rounded-tl-sm border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-800 whitespace-pre-wrap">
          {messageText}
        </div>
      </div>
    </section>
  );
}

function DashboardCard({ card, onChange, onDelete, onPageChange, onSubmitFeedback }) {
  const { response, data, config } = card
  const fieldKeys = useMemo(() => getFieldKeys(data), [data])
  const numericKeys = useMemo(() => getNumericKeys(data), [data])

  if (card.isError) return <ErrorResultCard card={card} onDelete={onDelete} />
  if (card.isMessage) return <MessageResultCard card={card} onDelete={onDelete} />

  return (
    <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
      <CardHeader card={card} onDelete={onDelete} onChange={onChange} onSubmitFeedback={onSubmitFeedback} />
      {card.normalization && card.normalization.was_modified && (
        <div className="border-b border-slate-100 px-4 py-2 md:px-5">
          <NormalizationBanner normalization={card.normalization} />
        </div>
      )}
      <div className="flex min-h-[360px] min-h-0">
        <LeftViewRail currentView={config.viewType} onChange={(viewType) => onChange({ viewType })} />
        <div className="min-w-0 flex-1 px-3 py-3 md:px-4">
          <div className="grid h-full min-h-0 gap-3 xl:grid-cols-[minmax(0,1fr)_280px]">
            <div className="min-h-0 min-w-0 rounded-[18px] border border-slate-200 bg-white p-3">
              <VisualizationSwitcher data={data} response={response} config={config} onPageChange={onPageChange} />
            </div>
            <div className="min-h-0 min-w-0 rounded-[18px] border border-slate-200 bg-slate-50 p-3">
              <AnalysisPanel card={card} onChange={onChange} />
            </div>
          </div>
        </div>
        <CustomizeDrawer config={config} fieldKeys={fieldKeys} numericKeys={numericKeys} onChange={onChange} />
      </div>
    </section>
  )
}

function ErrorResultCard({ card, onDelete }) {
  return (
    <section className="overflow-hidden rounded-[24px] border border-amber-200 bg-white shadow-sm">
      <div className="border-b border-amber-100 px-4 py-4 md:px-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-lg font-bold tracking-tight text-slate-900 md:text-2xl">{card.config?.title || promptToTitle(card.query)}</div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
                {card.mode === 'ai' ? 'AI mode' : 'Model mode'}
              </span>
              <span className="rounded-full bg-amber-100 px-3 py-1 text-xs text-amber-800">Unable to render result</span>
            </div>
          </div>
          <button onClick={onDelete} className="rounded-xl p-2 text-rose-500 transition hover:bg-rose-50" title="Delete card">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="px-4 py-5 md:px-5">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="text-sm font-semibold text-amber-900">We couldn’t complete this query</div>
          <p className="mt-1 text-sm text-amber-800">Please rephrase your question in a different way and try again.</p>
          <div className="mt-4 rounded-xl border border-amber-200 bg-white/70 px-3 py-2 text-xs text-slate-600">
            <div className="font-medium text-slate-700">Question</div>
            <div className="mt-1">{card.query}</div>
          </div>
          {card.technicalMessage ? (
            <details className="mt-3 rounded-xl border border-amber-200 bg-white/70 px-3 py-2 text-xs text-slate-600">
              <summary className="cursor-pointer font-medium text-slate-700">Technical details</summary>
              <div className="mt-2 whitespace-pre-wrap break-words">{card.technicalMessage}</div>
            </details>
          ) : null}
        </div>
      </div>
    </section>
  )
}

function CardHeader({ card, onDelete, onChange, onSubmitFeedback }) {
  const filters = card.response?.metadata_used?.filters || []
  const timing  = card.response?.timing
  const columns = card.response?.data?.columns || []
  const hasSalesValues = columns.some((column) => looksLikeSalesValueColumn(column))
  const salesUnit = card.config?.salesUnit || 'absolute'
  return (
    <div className="border-b border-slate-200 px-4 py-4 md:px-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-lg font-bold tracking-tight text-slate-900 md:text-2xl">{card.config.title}</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
              {card.mode === 'ai' ? 'AI mode' : 'Model mode'}
            </span>
            {filters.map((filter) => (
              <span key={`${filter.key}-${filter.value}`} className="rounded-full bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
                {filter.label}: {filter.value}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {card.messageId && onSubmitFeedback && (
            <FeedbackButtons messageId={card.messageId} onSubmit={onSubmitFeedback} />
          )}
          {hasSalesValues ? (
            <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1" title="Display sales values">
              {[
                { value: 'absolute', label: 'Absolute' },
                { value: 'lakh', label: 'Lakh' },
                { value: 'cr', label: 'Cr' },
              ].map((item) => (
                <button
                  key={item.value}
                  onClick={() => onChange({ salesUnit: item.value })}
                  className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition ${
                    salesUnit === item.value ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}
          {timing ? <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs text-slate-700">{timing.total_ms} ms</span> : null}
          <button onClick={onDelete} className="rounded-xl p-2 text-rose-500 transition hover:bg-rose-50" title="Delete card">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

function LeftViewRail({ currentView, onChange }) {
  return (
    <aside className="flex w-[66px] flex-col items-center gap-1 border-r border-slate-200 px-2 py-3">
      {VIEW_ITEMS.map((item) => {
        const Icon   = item.icon
        const active = currentView === item.value
        return (
          <button
            key={item.value}
            onClick={() => onChange(item.value)}
            title={item.label}
            className={`flex h-9 w-9 items-center justify-center rounded-xl transition ${
              active ? 'bg-rose-50 text-rose-500' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Icon className="h-4 w-4" />
          </button>
        )
      })}
      <div className="mt-auto flex flex-col items-center gap-2 pt-3 text-slate-400">
        <div className="-rotate-90 whitespace-nowrap text-[11px] font-medium tracking-wide">Views</div>
        <LayoutPanelLeft className="h-4 w-4" />
      </div>
    </aside>
  )
}

function AnalysisPanel({ card, onChange }) {
  const tab         = card.config.analysisTab || 'summary'
  const metadataUsed = card.response?.metadata_used || {}
  const columns     = card.response?.data?.columns || []
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-3 flex items-center gap-2">
        {ANALYSIS_TABS.map((item) => {
          const Icon   = item.icon
          const active = tab === item.value
          return (
            <button
              key={item.value}
              onClick={() => onChange({ analysisTab: item.value })}
              className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium transition ${
                active ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {item.label}
            </button>
          )
        })}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pr-1 space-y-3">
        {tab === 'summary' && (
          <>
            <InfoPanel title="Visualization decision">
              <div className="text-sm text-slate-700">{card.response?.visualization?.reason || 'Table is the default view.'}</div>
            </InfoPanel>
            <InfoPanel title="Measures used">
              <div className="flex flex-wrap gap-2">
                {(metadataUsed.measures || []).length
                  ? metadataUsed.measures.map((m) => <span key={m} className="rounded-full bg-white px-3 py-1 text-xs text-slate-700">{m}</span>)
                  : <span className="text-sm text-slate-500">No explicit measure detected.</span>}
              </div>
            </InfoPanel>
          </>
        )}
        {tab === 'analysis' && (
          <>
            <InfoPanel title="Query preview">
              {/*
                The generated query lives in a collapsible block (Req 5.2). Restored
                history messages reach this through `normalizeAssistantResponse`,
                which resolves `query || generated_sql` to '' when neither exists —
                hence the explicit empty-state instead of an empty <pre>.
              */}
              {card.response?.query ? (
                <details>
                  <summary className="cursor-pointer text-xs font-medium text-slate-700">Generated query</summary>
                  <pre className="mt-2 max-h-[260px] overflow-auto whitespace-pre-wrap text-[11px] leading-5 text-slate-700">{card.response.query}</pre>
                </details>
              ) : (
                <div className="text-sm text-slate-500">No query text was returned for this result.</div>
              )}
            </InfoPanel>
            <InfoPanel title="Timings">
              <div className="space-y-1 text-sm text-slate-700">
                <div>Query generation: {card.response?.timing?.query_generation_ms} ms</div>
                <div>Databricks execution: {card.response?.timing?.databricks_execution_ms} ms</div>
                <div>Total: {card.response?.timing?.total_ms} ms</div>
              </div>
            </InfoPanel>
          </>
        )}
        {tab === 'context' && (
          <>
            <InfoPanel title="Columns returned">
              <div className="flex flex-wrap gap-2">
                {columns.map((c) => <span key={c} className="rounded-full bg-white px-3 py-1 text-xs text-slate-700">{humanize(c)}</span>)}
              </div>
            </InfoPanel>
            <InfoPanel title="Grouping">
              <div className="space-y-1 text-sm text-slate-700">
                {(metadataUsed.group_by || []).length ? metadataUsed.group_by.map((g) => <div key={g}>{g}</div>) : 'No grouping applied.'}
              </div>
            </InfoPanel>
          </>
        )}
      </div>
    </div>
  )
}

function InfoPanel({ title, children }) {
  return (
    <section className="min-h-0 overflow-hidden rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{title}</div>
      {children}
    </section>
  )
}

function CustomizeDrawer({ config, fieldKeys, numericKeys, onChange }) {
  const [expanded, setExpanded] = useState(config.panelOpen ?? false)
  const [activeTab, setActiveTab] = useState('display')
  useEffect(() => { setExpanded(config.panelOpen ?? false) }, [config.panelOpen])
  const setField = (e) => {
    const patch = { [e.target.name]: e.target.value }
    if (['primaryColor', 'secondaryColor', 'accentColor'].includes(e.target.name)) {
      patch.paletteName = 'custom'
    }
    onChange(patch)
  }
  const setToggle = (e) => onChange({ [e.target.name]: e.target.checked })
  const setNumber = (e) => onChange({ [e.target.name]: Number(e.target.value) })
  const usableNumericKeys = numericKeys.length ? numericKeys : fieldKeys
  return (
    <aside className={`min-h-0 border-l border-slate-200 bg-[#fafafa] transition-all duration-300 ${expanded ? 'w-[290px]' : 'w-[46px]'}`}>
      {expanded ? (
        <div className="flex h-full min-h-0 flex-col overflow-hidden px-3 py-3">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">Customize</div>
              <div className="text-xs text-slate-500">Display and field mapping.</div>
            </div>
            <button
              onClick={() => { setExpanded(false); onChange({ panelOpen: false }) }}
              className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="mb-3 grid grid-cols-3 gap-2">
            {CUSTOMIZE_TABS.map((tab) => {
              const active = activeTab === tab.value
              return (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={`rounded-2xl border px-2 py-2 text-center text-[11px] font-medium transition ${
                    active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto pr-1 pb-2">
            {activeTab === 'display' && (
              <PanelSection title="Display">
                <CheckboxRow label="Show Tooltip"  name="showTooltip"  checked={config.showTooltip}  onChange={setToggle} />
                <CheckboxRow label="Show Labels"   name="showLabels"   checked={config.showLabels}   onChange={setToggle} />
                <CheckboxRow label="Show Legend"   name="showLegend"   checked={config.showLegend}   onChange={setToggle} />
                <CheckboxRow label="Show Axis"     name="showAxis"     checked={config.showAxis}     onChange={setToggle} />
                <LabeledSelect label="Orientation" name="orientation"  value={config.orientation}  onChange={setField} options={ORIENTATIONS} />
                <LabeledSelect
                  label="Color Palette" name="paletteName" value={config.paletteName} onChange={setField}
                  options={Object.keys(COLOR_PALETTES).map((key) => ({ value: key, label: key[0].toUpperCase() + key.slice(1) }))}
                />
                <ColorField label="Primary"   name="primaryColor"   value={config.primaryColor}   onChange={setField} />
                <ColorField label="Secondary" name="secondaryColor" value={config.secondaryColor} onChange={setField} />
                <ColorField label="Accent"    name="accentColor"    value={config.accentColor}    onChange={setField} />
                <NumberField label="Inner Radius" name="innerRadius" min={0}  max={140} value={config.innerRadius} onChange={setNumber} />
                <NumberField label="Outer Radius" name="outerRadius" min={40} max={180} value={config.outerRadius} onChange={setNumber} />
                <NumberField label="Bar Size"     name="barSize"     min={12} max={40}  value={config.barSize}     onChange={setNumber} />
              </PanelSection>
            )}
            {activeTab === 'mapping' && (
              <PanelSection title="Fields">
                <LabeledSelect label="Category" name="categoryField" value={config.categoryField} onChange={setField} options={fieldKeys.map((k) => ({ value: k, label: humanize(k) }))} />
                <LabeledSelect label="Value"    name="valueField"    value={config.valueField}    onChange={setField} options={usableNumericKeys.map((k) => ({ value: k, label: humanize(k) }))} />
                <LabeledSelect label="Compare"  name="compareField"  value={config.compareField}  onChange={setField} options={usableNumericKeys.map((k) => ({ value: k, label: humanize(k) }))} />
                <LabeledSelect label="Line Field" name="lineField"   value={config.lineField}     onChange={setField} options={usableNumericKeys.map((k) => ({ value: k, label: humanize(k) }))} />
                <LabeledSelect label="Sort By"  name="sortByField"   value={config.sortByField}   onChange={setField} options={fieldKeys.map((k) => ({ value: k, label: humanize(k) }))} />
                <div className="my-1 border-t border-slate-200" />
                <LabeledSelect label="X Field" name="xField"    value={config.xField}    onChange={setField} options={usableNumericKeys.map((k) => ({ value: k, label: humanize(k) }))} />
                <LabeledSelect label="Y Field" name="yField"    value={config.yField}    onChange={setField} options={usableNumericKeys.map((k) => ({ value: k, label: humanize(k) }))} />
                <LabeledSelect label="Size"    name="sizeField" value={config.sizeField} onChange={setField} options={usableNumericKeys.map((k) => ({ value: k, label: humanize(k) }))} />
              </PanelSection>
            )}
            {activeTab === 'chartView' && (
              <PanelSection title="Chart View">
                <div className="grid grid-cols-2 gap-2">
                  {VIEW_ITEMS.map((view) => {
                    const active = config.viewType === view.value
                    return (
                      <button
                        key={view.value}
                        onClick={() => onChange({ viewType: view.value })}
                        className={`rounded-2xl border px-3 py-2 text-sm font-medium transition ${
                          active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {view.label}
                      </button>
                    )
                  })}
                </div>
              </PanelSection>
            )}
          </div>
        </div>
      ) : (
        <div className="flex h-full flex-col items-center py-4">
          <button
            onClick={() => { setExpanded(true); onChange({ panelOpen: true }) }}
            className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-50"
            title="Open customize"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="mt-5 [writing-mode:vertical-rl] text-xs font-medium tracking-wide text-slate-500">Customize</div>
          <Settings2 className="mt-3 h-4 w-4 text-slate-400" />
        </div>
      )}
    </aside>
  )
}

function ChatComposer({ message, setMessage, onSend, onKeyDown, suggestions, activeSuggestionIndex, onSuggestionSelect, onDismissSuggestions, aiMode, setAiMode, isSubmitting, micStatus, onMicStatusChange, onTranscript }) {
  const activity = getWorkspaceActivityState({ micStatus, isSubmitting })
  const composerRef = useRef(null)

  useEffect(() => {
    if (!suggestions.length) return
    function handleClickOutside(e) {
      if (composerRef.current && !composerRef.current.contains(e.target)) {
        onDismissSuggestions?.()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [suggestions.length, onDismissSuggestions])

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto max-w-[920px] px-4 py-3">
        <div ref={composerRef} className="relative rounded-[28px] border border-slate-200 bg-white shadow-sm">
          {suggestions.length ? (
            <div className="absolute bottom-full left-4 right-4 mb-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
              <div className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Suggestions</div>
              <div className="space-y-1">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={`${suggestion.kind}-${suggestion.canonical_name}`}
                    onClick={() => onSuggestionSelect(suggestion)}
                    className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition ${
                      index === activeSuggestionIndex ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{suggestion.label}</div>
                      <div className={`truncate text-xs ${index === activeSuggestionIndex ? 'text-slate-300' : 'text-slate-500'}`}>
                        {suggestion.kind}
                      </div>
                    </div>
                    <div className={`rounded-full px-2 py-1 text-[10px] ${index === activeSuggestionIndex ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {suggestion.insert_text}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-3 px-4 py-3">
            <button
              onClick={() => setAiMode((v) => !v)}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-[11px] font-semibold transition ${
                aiMode ? 'bg-slate-900 text-white' : 'bg-[#f6f2e7] text-slate-700'
              }`}
              title="Toggle AI mode"
            >
              <span className={`inline-flex h-3.5 w-3.5 rounded-full border-2 ${aiMode ? 'border-white bg-white' : 'border-slate-300 bg-white'}`} />
              {aiMode ? 'AI MODE' : 'MODEL MODE'}
            </button>
            <div className="relative flex-1">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={onKeyDown}
                readOnly={activity.active}
                rows={1}
                placeholder="Ask a question here"
                className={`max-h-32 min-h-[40px] w-full resize-none bg-transparent py-2 text-sm outline-none placeholder:text-slate-400 ${activity.active ? 'select-none text-transparent caret-transparent' : ''}`}
              />
              <AnimatedInputOverlay
                active={activity.active}
                title={activity.title}
                subtitle={activity.subtitle}
                roundedClassName="rounded-2xl"
              />
            </div>
            <AudioRecorderButton
              compact
              disabled={isSubmitting}
              onStatusChange={onMicStatusChange}
              onTranscript={(transcript) => onTranscript?.(transcript)}
            />
            <button
              onClick={() => onSend()}
              disabled={activity.active}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              title="Submit"
            >
              <SendHorizontal className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
